import { useState, useContext } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Chip,
  Box,
  Typography,
} from "@mui/material";
import RowActionsMenu from "../UI/RowActionsMenu";
import EmptyState from "../UI/EmptyState";
import SearchBar from "../UI/SearchBar";
import EditTransferRequestDialog from "./EditTransferRequestDialog";
import DeleteTransferRequestDialog from "./DeleteTransferRequestDialog";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import { LoginContext } from "../../Components/Login/LoginContext";

const TransferRequestsTable = (props) => {
  const [auth] = useContext(LoginContext);
  const [editRow, setEditRow] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const rows = props.transferRequests.map(
    ({ id, player, team, playerAccepted, teamAccepted, requestDate }) => ({
      id,
      player,
      team,
      playerAccepted,
      teamAccepted,
      requestDate,
    })
  );

  const transferRequestsWithPermission =
    props.transferRequestsWithPermission.map((tr) => tr.id);

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={<SwapHorizRoundedIcon />}
        message="No transfer requests yet."
      />
    );
  }

  const statusFiltered = rows.filter((row) => {
    if (statusFilter === "pending") return !row.playerAccepted || !row.teamAccepted;
    if (statusFilter === "accepted") return row.playerAccepted && row.teamAccepted;
    return true;
  });

  const q = search.toLowerCase();
  const filteredRows = statusFiltered.filter(
    (row) =>
      row.player.name.toLowerCase().includes(q) ||
      row.team.name.toLowerCase().includes(q)
  );

  const noResults = filteredRows.length === 0;

  const statuses = [
    { value: "all", label: "All" },
    { value: "pending", label: "Pending" },
    { value: "accepted", label: "Accepted" },
  ];

  return (
    <>
      <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
        {statuses.map((s) => (
          <Chip
            key={s.value}
            label={s.label}
            onClick={() => setStatusFilter(s.value)}
            color={statusFilter === s.value ? "primary" : "default"}
            variant={statusFilter === s.value ? "filled" : "outlined"}
          />
        ))}
      </Box>
      <SearchBar
        value={search}
        onChange={setSearch}
        placeholder="Search by player or team..."
      />

      {noResults ? (
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          No transfer requests match &ldquo;{search}&rdquo;.
        </Typography>
      ) : (
        <TableContainer
          className="tableContainer"
          data-testid="transferRequestsTable"
          component={Paper}
          sx={{ overflowX: "auto" }}
        >
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead>
              <TableRow>
                {transferRequestsWithPermission.length > 0 && (
                  <TableCell sx={{ width: 48, p: 0.5 }} />
                )}
                <TableCell>Player Name</TableCell>
                <TableCell>Team Name</TableCell>
                <TableCell>Player Accepted</TableCell>
                <TableCell>Team Accepted</TableCell>
                <TableCell>Request Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row) => (
                <TableRow
                  data-testid={"transferRequestsTableRow" + row.id}
                  hover
                  key={row.id}
                  sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
                >
                  {transferRequestsWithPermission.length > 0 && (
                    <TableCell sx={{ width: 48, p: 0.5 }}>
                      {transferRequestsWithPermission.includes(row.id) && (
                        <RowActionsMenu
                          onEdit={() => setEditRow(row)}
                          onDelete={() => setDeleteRow(row)}
                        />
                      )}
                    </TableCell>
                  )}
                  <TableCell>{row.player.name}</TableCell>
                  <TableCell>{row.team.name}</TableCell>
                  <TableCell>
                    <Checkbox checked={row.playerAccepted} disabled />
                  </TableCell>
                  <TableCell>
                    <Checkbox checked={row.teamAccepted} disabled />
                  </TableCell>
                  <TableCell>{row.requestDate}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {editRow && (
        <EditTransferRequestDialog
          open
          setOpen={(open) => !open && setEditRow(null)}
          transferRequest={editRow}
          players={props.players}
          teams={props.teams}
        />
      )}
      {deleteRow && (
        <DeleteTransferRequestDialog
          open
          setOpen={(open) => !open && setDeleteRow(null)}
          transferRequest={deleteRow}
        />
      )}
    </>
  );
};

export default TransferRequestsTable;
